import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import {
  CircleDot,
  Trophy,
  CheckCircle2,
  Loader2,
  UserPlus,
  FileText,
} from "lucide-react";

/**
 * AddToPipelineModal - Standardized way to add contacts to the pipeline
 */
export default function AddToPipelineModal({
  open,
  onOpenChange,
  contact,
  token,
  intakeForms = [],
  leadForms = [],
  onSuccess,
}) {
  const [pipelineAction, setPipelineAction] = useState(null);
  const [sourceDetail, setSourceDetail] = useState("none");
  const [sendIntakeEmail, setSendIntakeEmail] = useState(true);
  const [selectedFormId, setSelectedFormId] = useState("");
  // Lead form state (for "Create Lead" action)
  const [sendLeadForm, setSendLeadForm] = useState(false);
  const [selectedLeadFormId, setSelectedLeadFormId] = useState("");
  const [loading, setLoading] = useState(false);
  const [showWonOptions, setShowWonOptions] = useState(false);
  const [showLeadOptions, setShowLeadOptions] = useState(false);

  const handleSelectAction = (action) => {
    setPipelineAction(action);
    setShowWonOptions(action === "won");
    setShowLeadOptions(action === "lead");
    // Auto-select first form if available
    if (action === "lead" && leadForms.length === 1) {
      setSelectedLeadFormId(leadForms[0].id);
    }
    if (action === "won" && intakeForms.length === 1) {
      setSelectedFormId(intakeForms[0].id);
    }
  };

  const handleSubmit = async () => {
    if (!pipelineAction) {
      toast.error("Please select an option");
      return;
    }
    
    if (!contact?.id) {
      toast.error("No contact selected");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/contacts/${contact.id}/add-to-pipeline`,
        {
          pipeline_action: pipelineAction,
          source_detail: sourceDetail === "none" ? null : sourceDetail,
          send_intake_email: pipelineAction === "won" ? sendIntakeEmail : false,
          intake_form_id: pipelineAction === "won" && sendIntakeEmail ? selectedFormId : null,
          // Lead form options
          send_lead_form: pipelineAction === "lead" ? sendLeadForm : false,
          lead_form_id: pipelineAction === "lead" && sendLeadForm ? selectedLeadFormId : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === "success") {
        toast.success(response.data.message);
        
        if (response.data.email_sent) {
          toast.success(pipelineAction === "lead" ? "Lead form sent to client" : "Intake email sent to client");
        } else if (response.data.email_error) {
          toast.error(`Failed to send email: ${response.data.email_error}`);
        }

        onSuccess?.(response.data.submission);
        resetAndClose();
      }
    } catch (error) {
      console.error("Failed to add to pipeline:", error);
      const errorDetail = error.response?.data?.detail;
      
      // Handle structured error responses
      if (typeof errorDetail === 'object' && errorDetail !== null) {
        if (errorDetail.error === 'active_submission_exists') {
          toast.error(errorDetail.message || "This contact already has an active submission");
        } else {
          toast.error(errorDetail.message || "Failed to add to pipeline");
        }
      } else {
        toast.error(errorDetail || "Failed to add to pipeline");
      }
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setPipelineAction(null);
    setSourceDetail("none");
    setSendIntakeEmail(true);
    setSelectedFormId("");
    setSendLeadForm(false);
    setSelectedLeadFormId("");
    setShowWonOptions(false);
    setShowLeadOptions(false);
    onOpenChange(false);
  };

  // Only close when Dialog signals close (not open)
  const handleDialogChange = (isOpen) => {
    if (!isOpen) {
      resetAndClose();
    }
  };

  const sourceOptions = [
    { value: "none", label: "Not specified" },
    { value: "walk-in", label: "Walk-in" },
    { value: "phone-call", label: "Phone call" },
    { value: "referral", label: "Referral" },
    { value: "other", label: "Other" },
  ];

  // Determine button text
  const getButtonText = () => {
    if (!pipelineAction) return "Add to Pipeline";
    if (pipelineAction === "lead") {
      return sendLeadForm ? "Create Lead & Send Form" : "Create Lead";
    }
    if (pipelineAction === "won") {
      return sendIntakeEmail ? "Mark Won & Send Intake" : "Mark as Won";
    }
    if (pipelineAction === "completed") {
      return "Log as Completed";
    }
    return "Add to Pipeline";
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="sm:max-w-[450px]" data-testid="add-to-pipeline-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-primary" />
            Add to Pipeline
          </DialogTitle>
          <DialogDescription>
            Add {contact?.name || "this contact"} to your pipeline
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Action Selection */}
          <div className="space-y-2">
            {/* Create Lead Option */}
            <button
              type="button"
              onClick={() => handleSelectAction("lead")}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                pipelineAction === "lead"
                  ? "border-primary bg-primary/5"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              data-testid="pipeline-action-lead"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <CircleDot className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="font-medium text-sm">Create Lead</p>
                <p className="text-xs text-muted-foreground">Qualify this client before proceeding</p>
              </div>
            </button>

            {/* Mark as Won Option */}
            <button
              type="button"
              onClick={() => handleSelectAction("won")}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                pipelineAction === "won"
                  ? "border-primary bg-primary/5"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              data-testid="pipeline-action-won"
            >
              <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="font-medium text-sm">Mark as Won</p>
                <p className="text-xs text-muted-foreground">Log this client as closed</p>
              </div>
            </button>

            {/* Log as Completed Option */}
            <button
              type="button"
              onClick={() => handleSelectAction("completed")}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                pipelineAction === "completed"
                  ? "border-primary bg-primary/5"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              data-testid="pipeline-action-completed"
            >
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="font-medium text-sm">Log as Completed Service</p>
                <p className="text-xs text-muted-foreground">Add as already completed</p>
              </div>
            </button>
          </div>

          {/* Source Selection (optional) */}
          <div className="space-y-2 pt-2">
            <Label className="text-sm text-muted-foreground">Source (optional)</Label>
            <Select value={sourceDetail} onValueChange={setSourceDetail}>
              <SelectTrigger data-testid="source-select">
                <SelectValue placeholder="How did they reach you?" />
              </SelectTrigger>
              <SelectContent>
                {sourceOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Lead Options - Send Lead Form */}
          {showLeadOptions && (
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-start gap-3 pt-3">
                <Checkbox
                  id="send-lead-form"
                  checked={sendLeadForm}
                  onCheckedChange={(checked) => setSendLeadForm(checked === true)}
                  data-testid="send-lead-form-checkbox"
                />
                <div className="space-y-1">
                  <Label htmlFor="send-lead-form" className="font-medium cursor-pointer">
                    Send lead form to client
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Collect qualifying information before proceeding
                  </p>
                </div>
              </div>

              {sendLeadForm && leadForms && leadForms.length > 0 && (
                <div className="pl-7">
                  <Label className="text-xs text-muted-foreground mb-1 block">Select Form</Label>
                  <Select value={selectedLeadFormId} onValueChange={setSelectedLeadFormId}>
                    <SelectTrigger className="w-full" data-testid="lead-form-select">
                      <SelectValue placeholder="Choose lead form..." />
                    </SelectTrigger>
                    <SelectContent>
                      {leadForms.map((form) => (
                        <SelectItem key={form.id} value={form.id}>
                          {form.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {leadForms.length === 1 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Only one lead form available - auto-selected
                    </p>
                  )}
                </div>
              )}

              {sendLeadForm && (!leadForms || leadForms.length === 0) && (
                <p className="text-xs text-amber-600 pl-7">
                  No lead forms available. Create a pre-booking form first.
                </p>
              )}
            </div>
          )}

          {/* Won Options - Send Intake Email */}
          {showWonOptions && (
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-start gap-3 pt-3">
                <Checkbox
                  id="send-intake"
                  checked={sendIntakeEmail}
                  onCheckedChange={(checked) => setSendIntakeEmail(checked === true)}
                  data-testid="send-intake-checkbox"
                />
                <div className="space-y-1">
                  <Label htmlFor="send-intake" className="font-medium cursor-pointer">
                    Send intake form to client
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Start the intake workflow automatically
                  </p>
                </div>
              </div>

              {sendIntakeEmail && intakeForms && intakeForms.length > 0 && (
                <div className="pl-7">
                  <Label className="text-xs text-muted-foreground mb-1 block">Select Form</Label>
                  <Select value={selectedFormId} onValueChange={setSelectedFormId}>
                    <SelectTrigger className="w-full" data-testid="intake-form-select">
                      <SelectValue placeholder="Choose intake form..." />
                    </SelectTrigger>
                    <SelectContent>
                      {intakeForms.map((form) => (
                        <SelectItem key={form.id} value={form.id}>
                          {form.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {sendIntakeEmail && (!intakeForms || intakeForms.length === 0) && (
                <p className="text-xs text-amber-600 pl-7">
                  No intake forms available. Create one first.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={resetAndClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!pipelineAction || loading}
            data-testid="confirm-add-to-pipeline"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {getButtonText()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
